import { FC, useEffect, useRef, useState } from "react";
import { IEntitySimpleDTO } from "../../libs/coreapi-dto/@types/common";
import useGridFilter, { CreateSelectorGridFilter } from "../../system/hooks/useGridFilter";
import styles from './GridMultipleSelectorInput.module.scss'
import renderGlobalAlert from "../../system/hooks/useGlobalAlert";
import { CrossButton } from "../buttons/iconButtons/other/PanelCollection";
import { Button } from "../buttons/button";

const GridMultipleSelectorInput: FC<IGridMultipleSelectorInputProps> = (props) => {

    const [visible, setVisible] = useState<boolean>(false);
    const [gridFilter, dispatchGridFilter] = useGridFilter(CreateSelectorGridFilter);
    const [isEnterPressed, setIsEnterPressed] = useState<boolean>(false);
    const [internalEntities, setInternalEntities] = useState<IEntitySimpleDTO[]>(props.selectedEntities || []);
    const [tempSelectedEntities, setTempSelectedEntities] = useState<IEntitySimpleDTO[]>([]);

    const listRef = useRef<HTMLDivElement | null>(null);
    const firstItemRef = useRef<HTMLDivElement | null>(null);
    const [isWrapped, setIsWrapped] = useState<boolean>(false);

    useEffect(() => {
        setInternalEntities(props.selectedEntities || []);
    }, [props.selectedEntities]);

    useEffect(() => {
        if (props.label?.includes(`Склады`) ||
            props.label?.includes(`Аптеки`) ||
            props.label?.includes(`Поставщики`) ||
            props.label?.includes(`Получатели`)) {

            dispatchGridFilter({
                type: "newGridFilters",
                payload: {
                    ...gridFilter,
                    numberPerPage: 300,
                }
            });
        }
    }, [props.label]);

    useEffect(() => {
        const measure = () => {
            const listEl = listRef.current;
            if (!listEl) return;
            const singleRowHeight = firstItemRef.current?.offsetHeight || 0;
            const currentHeight = listEl.clientHeight;
            if (singleRowHeight > 0) {
                setIsWrapped(currentHeight > singleRowHeight + 2);
            } else {
                setIsWrapped(false);
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [internalEntities]);

    const handleOpenSelector = () => {
        if (props.selectorModalJsx) {
            props.onFocus?.()
            setIsEnterPressed(true)
            setVisible(true)
            setTempSelectedEntities([...internalEntities])
        } else {
            const msg = 'Плагин не реализован!!!'
            renderGlobalAlert({
                variant: "warning",
                statusCode: 500,
                title: msg,
                detail: msg
            })
        }
    };

    const handleClear = () => {
        setInternalEntities([]);
        props.onClear?.()
        props.onSelect?.([])
        dispatchGridFilter({ type: 'deleteColumnFilter', payload: props.defaultPropertyName ?? 'name' })
    };

    const renderEntityList = () => {
        return (
            <div className={`${styles.container} ${props.inline ? styles.inline : ''}`}>
                {!props.inline && (
                    <div className={styles.headerOnlyLabel}>
                        <label className={styles.label}>{props.label}</label>
                    </div>
                )}
                <div className={styles.mainRow}>
                    {props.inline && (
                        <label className={styles.labelInline}>{props.label}</label>
                    )}
                    <div className={`${styles.formRow} ${isWrapped ? styles.wrapped : styles.singleLine}`}>
                        <div className={styles.selectedItemsContainer}>
                            <div ref={listRef} className={styles.selectedItemsList}>
                                {
                                    internalEntities.map((x, index) => {
                                        const itemProps = index === 0 ? { ref: firstItemRef } : {};
                                        return <div key={x.idGlobal} className={styles.selectedItem} {...itemProps}>
                                            <span className={styles.itemText}>{x.displayName}</span>
                                            <div className={styles.deleteButton}>
                                                <CrossButton
                                                    sizeVariant="micro"
                                                    colorVariant="danger"
                                                    onClick={() => {
                                                        const updatedEntities = internalEntities.filter(y => y.idGlobal !== x.idGlobal);
                                                        setInternalEntities(updatedEntities);
                                                        props.onSelect?.(updatedEntities);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    })
                                }
                            </div>
                        </div>
                        <div className={styles.actions}>
                            <Button
                                variant={"outprimary"}
                                onClick={handleOpenSelector}
                                disabled={props.disabled}
                            >
                                Добавить
                            </Button>
                            {internalEntities.length > 0 && (
                                <Button
                                    variant={"danger"}
                                    onClick={handleClear}
                                    disabled={props.disabled}
                                >
                                    Очистить
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    }
    
    return <>
        {renderEntityList()}
        {
            visible && props.selectorModalJsx &&
            <props.selectorModalJsx
                data={props.data}
                masterIdGlobal={props.masterIdGlobal}
                gridFilter={props.gridFilter ?? gridFilter}
                selectedEntity={internalEntities?.[0]}
                selectedEntities={tempSelectedEntities}
                multipleSelect={true}
                ok={(entity, additionalData) => {
                    setVisible(false)
                    
                    if (Array.isArray(entity)) {
                        setInternalEntities(entity);
                        props.onSelect?.(entity, additionalData);
                    } 
                    else if (entity && typeof entity === 'object' && entity.idGlobal) {
                        const newEntities = [entity];
                        setInternalEntities(newEntities);
                        props.onSelect?.(newEntities, additionalData);
                    } else {
                        setInternalEntities([]);
                        props.onSelect?.([], additionalData);
                    }
                    
                    setTempSelectedEntities([])
                }}
                cancel={() => {
                    setVisible(false)
                    setTempSelectedEntities([])
                }}
                treeViewCheckDirectoryType={props.treeViewCheckDirectoryType}
                isEnterPressed={isEnterPressed}
                setIsEnterPressed={() => setIsEnterPressed(false)}
                activateGroupProcessing={props.activateGroupProcessing}
                hideGroupProcessing={props.hideGroupProcessing}
                viewMode={props.viewMode}
                isSearchForMultyselect={props.isSearchForMultyselect}
                dataFromPluginGridSelectorInput={props.dataFromPluginGridSelectorInput}
                onMultipleSelect={(rows) => {
                    const mappedRows = rows ? rows.map(x => ({ 
                        idGlobal: x.idGlobal, 
                        displayName: x.displayName 
                    } as IEntitySimpleDTO)) : [];
                    setTempSelectedEntities(mappedRows)
                }}
            />
        }
    </>
}

export default GridMultipleSelectorInput